import { IForms } from 'src/app/models';
import { Injectable } from '@angular/core';
import { HelperService } from './helper.service';
import { Form } from '../models';
import { Observable, Subject, Subscription } from 'rxjs';
import { Field } from '../models/field.model';
import { DatabaseService } from './database.service';
import { limitToLast, orderByChild } from '@angular/fire/database';
import { StorageService } from './storage.service';
import { SafeUrl } from '@angular/platform-browser';
import { LocalAttachment } from './camera.service';

export interface IFormDefiniton {
  id: string;
  createdBy: string;
  creationDate: string;
  status: "active" | "archived";
  title: string;
  isDefault?: boolean;
  isPublic?: boolean;
}

export interface IFormRevision {
  id: string;
  creationDate: string;
  createdBy: string;
  fields: Field[];
}

export interface IUserForm {
  id: string;
  title: string;
  createdBy: string;
  creationDate: string;
  lastModifiedOn?: string;
  lastModifiedBy?: string;
  submittedOn? : string;
  formId: string;
  revisionId: string;
  organization: string;
  status: "assigned" | "submitted" | "incomplete";
  values?: {
    [fieldId: string]: string
  }
}

export interface IFormAttachmentResponse {
  creationDate: string,
  url: string,
  fileName: string,
  type: string,
}

@Injectable({
  providedIn: 'root'
})
export class FormService {

  public userFormsList: Observable<any>;
  public userFormsListSubscription: Subscription;
  public userForms: any[];
  public userFormsSubject = new Subject<any>();

  constructor(
    private dbService: DatabaseService,
    private helperService: HelperService,
    private storageService: StorageService,
  ) { }

  /* User Forms */
  public loadUserForms(userId): void {
    this.userFormsList = this.getUserFormsList(userId);
    this.userFormsListSubscription = this.userFormsList.subscribe(snap => {
      this.userForms = this.helperService.transformFirebaseObjectToArray(snap);
      this.publishUserFormsSubject();
    });
  }

  private publishUserFormsSubject() {
    this.userFormsSubject.next(this.userForms);
  }

  public getUserFormsSubject(): Subject<any> {
    return this.userFormsSubject;
  }

  public getUserFormsList(userId: string) {
    return this.dbService.getListByRefTest<any>(`userForms/${userId}`);
  }

  public clearUserForms() {
    this.userForms = null;
    this.userFormsList = null;
    if(this.userFormsListSubscription) {
      this.userFormsListSubscription.unsubscribe();
    }
  }

  /* Used when creating and submitting a user form directly */
  createUserForm(userId: string, formDetails: Partial<IUserForm>) {
    return this.dbService.pushByRef(`userForms/${userId}`, {
      ...formDetails
    })
  }

  /* Used for assigning a form from a trainer to a client */
  assignUserForm(orgId: string, formTitle: string, formId: string, userId: string, createdBy: string) {
    return this.dbService.pushByRef(`userForms/${userId}`, {
      status: "assigned",
      createdBy: createdBy,
      creationDate: new Date().toISOString(),
      formId: formId,
      organization: orgId,
      title: formTitle,
    });
  }

  deleteUserForm(userId: string, formId: string) {
    return this.dbService.removeRef(`userForms/${userId}/${formId}`);
  }
  
  public getUserFormAsPromise(userId: string, formId: string) {
    return this.dbService.getSnapshot(`userForms/${userId}/${formId}`);
  }

  public async getUserFormsAsPromise(userId: string) {
    const snap = await this.dbService.getSnapshot(`/userForms/${userId}`);
    let reports = this.helperService.transformFirebaseObjectToArray(snap);
    return reports.map((report) => {
      return report;
    });
  }

  public async updateUserForm(userId: string, formId: string, toUpdate: Partial<IUserForm>): Promise<any> {
    return this.dbService.updateRef(`userForms/${userId}/${formId}`, toUpdate);
  }

  public submitUserForm(userId: string, formName: string, formRevision: string, formValues: any) {
    return this.dbService.setRef(`${formName}/${userId}`, {
      formId: formRevision,
      values: formValues,
      creationDate: new Date().toISOString()
    });
  }


  /* Organization Forms */
  public async getFormDefinitions(orgId: string): Promise<IFormDefiniton[]> {
    const snap = await this.dbService.getSnapshot(`formDefinitions/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  public getFormDefinitionsList(orgId: string) {
    return this.dbService.getListByRef<IFormDefiniton>(`formDefinitions/${orgId}`, "id");
  }

  public async getFormDefinition(orgId: string, formId: string): Promise<IFormDefiniton> {
    const snap = await this.dbService.getSnapshot(`formDefinitions/${orgId}/${formId}`);
    return snap? {...snap, id: formId} : null;
  }

  public async getMostRecentFormRevision(orgId: string, formId: string): Promise<IFormRevision> {
    const snap = await this.dbService.getSnapshot(`formRevisions/${orgId}/${formId}`, orderByChild("creationDate"), limitToLast(1));
    return snap? this.helperService.transformFirebaseObjectToArray(snap)[0] : null;
  }

  public async getFormRevision(orgId: string, formId: string, revisionId: string): Promise<IFormRevision> {
    const snap = await this.dbService.getSnapshot(`formRevisions/${orgId}/${formId}/${revisionId}`);
    return snap? {...snap, id: revisionId} : null;
  }

  public async getFormRevisions(orgId: string): Promise<IFormRevision[]> {
    const snap = await this.dbService.getSnapshot(`formRevisions/${orgId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }
  
  public async getFormAsPromise(organizationName: string, formName: string): Promise<Form> {
    const snap = await this.dbService.getSnapshot(`forms/${organizationName}/${formName}`, orderByChild("creationDate"), limitToLast(1));
    return snap? new Form(this.helperService.transformFirebaseObjectToArray(snap)[0]) : null;
  }

  public updateOrganizationForm(orgId: string, formId: string, values: Partial<IFormDefiniton>): Promise<void> {
    return this.dbService.updateRef(`formDefinitions/${orgId}/${formId}`, values);
  }

  public removeOrganizationForm(orgId: string, formId: string){
    return this.dbService.removeRef(`formDefinitions/${orgId}/${formId}`);
  }
  
  async addOrganizationLibraryForms(orgId: string, userId:string, data: IForms) {
    return this.dbService.pushByRef(`formDefinitions/${orgId}/`, {
      createdBy: userId,
      creationDate: new Date().toISOString(),       
      ...data
    });
  }

  async getOrganizationLibraryFormsAsPromise(orgId: string, formId: string): Promise<IForms> {
    const snap = await this.dbService.getSnapshot(`formDefinitions/${orgId}/${formId}`);
    return snap? {...snap, uid: formId } : null;
  }
  
  async addOrganizationLibraryFormRevisions(orgId: string,formId: string, userId:string, data:IFormRevision ) {  
    return this.dbService.pushByRef(`formRevisions/${orgId}/${formId}/`, {
      createdBy: userId,
      creationDate: new Date().toISOString(),       
      fields:{...data}
    });
  }

  async addUserFormAttachment(userId: string, formId: string, attachment: LocalAttachment): Promise<IFormAttachmentResponse> {

    const storagePath = `users/${userId}/forms/${formId}/${attachment.name}`;
    let attachmentRef: string;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    return {
      creationDate: new Date().toISOString(),
      url: attachmentRef,
      fileName: attachment.name,
      type: attachment.type,
    }
  }
}
